import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { UserRole, JWTPayload } from '@cbm/shared';

// Auth state types
export interface AuthUser {
  userId: string;
  email: string;
  role: UserRole;
  roles?: string[];
  tenantId: string;
  fullName?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  user: AuthUser | null;
  token: string | null;
  isLoading: boolean;
  error: string | null;
}

// Auth actions
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: AuthUser; token: string } }
  | { type: 'AUTH_ERROR'; payload: string }
  | { type: 'AUTH_LOGOUT' }
  | { type: 'CLEAR_ERROR' };

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null,
  isLoading: false,
  error: null,
};

// Auth reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        token: action.payload.token,
        isLoading: false,
        error: null,
      };
    case 'AUTH_ERROR':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        token: null,
        isLoading: false,
        error: action.payload,
      };
    case 'AUTH_LOGOUT':
      return {
        ...initialState,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
}

// Auth context type
interface AuthContextType extends AuthState {
  login: (email: string, otp: string) => Promise<void>;
  logout: () => void;
  requestOTP: (fullName: string, email: string, mobile: string) => Promise<void>;
  clearError: () => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// JWT token utilities
const TOKEN_KEY = 'cbm_auth_token';

function parseJWT(token: string): JWTPayload | null {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to parse JWT:', error);
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = parseJWT(token);
  if (!payload) return true;
  
  const currentTime = Math.floor(Date.now() / 1000);
  return payload.exp < currentTime;
}

// Auth provider component
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Initialize auth state from localStorage
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token && !isTokenExpired(token)) {
      const payload = parseJWT(token);
      if (payload) {
        const user: AuthUser = {
          userId: payload.userId,
          email: payload.email,
          role: payload.role,
          roles: payload.roles ? payload.roles.split(',') : [payload.role],
          tenantId: payload.tenantId,
        };
        dispatch({ type: 'AUTH_SUCCESS', payload: { user, token } });
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    } else if (token) {
      // Token exists but is expired
      localStorage.removeItem(TOKEN_KEY);
    }
  }, []);

  // Request OTP
  const requestOTP = async (fullName: string, email: string, mobile: string) => {
    dispatch({ type: 'AUTH_START' });
    
    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/request-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ fullName, email, mobile }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to request OTP');
      }

      dispatch({ type: 'AUTH_ERROR', payload: '' });
    } catch (error) {
      dispatch({ 
        type: 'AUTH_ERROR', 
        payload: error instanceof Error ? error.message : 'Failed to request OTP' 
      });
      throw error;
    }
  };

  // Login with OTP
  const login = async (email: string, otp: string) => {
    dispatch({ type: 'AUTH_START' });

    try {
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Authentication failed');
      }

      const { token } = data.data;
      const payload = parseJWT(token);

      if (!payload) {
        throw new Error('Invalid token received');
      }

      const user: AuthUser = {
        userId: payload.userId,
        email: payload.email,
        role: payload.role,
        roles: payload.roles ? payload.roles.split(',') : [payload.role],
        tenantId: payload.tenantId,
      };

      localStorage.setItem(TOKEN_KEY, token);
      dispatch({ type: 'AUTH_SUCCESS', payload: { user, token } });
    } catch (error) {
      dispatch({ 
        type: 'AUTH_ERROR', 
        payload: error instanceof Error ? error.message : 'Authentication failed' 
      });
      throw error;
    }
  };

  // Logout
  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    dispatch({ type: 'AUTH_LOGOUT' });
    // Navigate to home instead of reloading to avoid access denied errors
    window.location.href = '/';
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    requestOTP,
    clearError,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

// Hook to use auth context
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}